export type PdfReportRow = {
  source: string;
  collectionOrTable: string;
  id: string | number | null;
  fieldOrColumn: string;
  value: string;
  newValue?: string;
  isSpecialCase?: boolean;
  specialReasons?: string[];
};

export type PdfReportInput = {
  title: string;
  subtitle?: string;
  from?: string;
  to?: string;
  mode?: string;
  environment?: string;
  durationMs?: number;
  note?: string;
  safeRows: PdfReportRow[];
  specialRows: PdfReportRow[];
};

function escapeHtml(text: unknown): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * PDF-only: for MySQL keep only Source_en rows (one per table+ID).
 * Drops Target_* / other Source_* language columns that repeat the same record.
 */
export function dedupeMysqlPdfRows(rows: PdfReportRow[]): PdfReportRow[] {
  const result: PdfReportRow[] = [];
  const seenSourceEn = new Set<string>();

  for (const row of rows) {
    if (row.source !== 'mysql') {
      result.push(row);
      continue;
    }

    if (row.fieldOrColumn !== 'Source_en') continue;

    const idKey = `${row.collectionOrTable}::${row.id ?? ''}`;
    if (seenSourceEn.has(idKey)) continue;
    seenSourceEn.add(idKey);
    result.push(row);
  }

  return result;
}

function renderTable(rows: PdfReportRow[], includeNewValue: boolean): string {
  if (!rows.length) {
    return '<p class="empty">None</p>';
  }

  const newHeader = includeNewValue ? '<th>New value</th>' : '';
  const body = rows
    .map((row) => {
      const reasons =
        row.specialReasons?.length
          ? `<div class="reasons">${escapeHtml(row.specialReasons.join(', '))}</div>`
          : '';
      const newCell = includeNewValue
        ? `<td class="value">${escapeHtml(row.newValue ?? '')}</td>`
        : '';
      return `<tr class="${row.isSpecialCase ? 'special' : ''}">
        <td>${escapeHtml(row.source)}</td>
        <td>${escapeHtml(row.collectionOrTable)}</td>
        <td>${escapeHtml(row.id)}</td>
        <td>${escapeHtml(row.fieldOrColumn)}${reasons}</td>
        <td class="value">${escapeHtml(row.value)}</td>
        ${newCell}
      </tr>`;
    })
    .join('');

  return `<table>
    <thead>
      <tr>
        <th>Source</th>
        <th>Collection / Table</th>
        <th>ID</th>
        <th>Field / Column</th>
        <th>Value</th>
        ${newHeader}
      </tr>
    </thead>
    <tbody>${body}</tbody>
  </table>`;
}

/**
 * Opens a print-ready HTML report. Use the browser's "Save as PDF" in the print dialog.
 */
export function downloadRenameContentPdf(input: PdfReportInput): void {
  const safeRows = dedupeMysqlPdfRows(input.safeRows);
  const specialRows = dedupeMysqlPdfRows(input.specialRows);
  const includeNewValue =
    safeRows.some((r) => r.newValue != null) || specialRows.some((r) => r.newValue != null);

  const safeLabel =
    input.mode === 'apply'
      ? '1. Applied (safe)'
      : input.mode === 'rollback'
        ? '1. Restored (safe)'
        : '1. Safe matches';
  const specialLabel =
    input.mode === 'apply'
      ? '2. Applied (special cases)'
      : input.mode === 'rollback'
        ? '2. Restored (special cases)'
        : '2. Special cases';
  const safeCountLabel =
    input.mode === 'apply' ? 'Applied safe' : input.mode === 'rollback' ? 'Restored safe' : 'Safe matches';
  const specialCountLabel =
    input.mode === 'apply'
      ? 'Applied special'
      : input.mode === 'rollback'
        ? 'Restored special'
        : 'Special cases';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(input.title)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; font-size: 11px; }
    h1 { font-size: 18px; margin: 0 0 6px; }
    h2 { font-size: 14px; margin: 24px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    .meta { color: #444; margin-bottom: 16px; line-height: 1.45; }
    .note { background: #fff8e1; border: 1px solid #ffe082; padding: 8px 10px; margin: 12px 0; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    th, td { border: 1px solid #ccc; padding: 5px 6px; vertical-align: top; word-wrap: break-word; }
    th { background: #f3f3f3; text-align: left; }
    tr.special { background: #fff3e0; }
    .value { white-space: pre-wrap; }
    .reasons { margin-top: 4px; color: #b71c1c; font-size: 10px; }
    .empty { color: #777; font-style: italic; }
    @media print {
      body { margin: 12px; }
      h2 { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(input.title)}</h1>
  <div class="meta">
    ${input.subtitle ? `<div>${escapeHtml(input.subtitle)}</div>` : ''}
    ${input.from != null ? `<div><strong>From:</strong> ${escapeHtml(input.from)}</div>` : ''}
    ${input.to != null ? `<div><strong>To:</strong> ${escapeHtml(input.to)}</div>` : ''}
    ${input.mode ? `<div><strong>Mode:</strong> ${escapeHtml(input.mode)}</div>` : ''}
    ${input.environment ? `<div><strong>Environment:</strong> ${escapeHtml(input.environment)}</div>` : ''}
    ${input.durationMs != null ? `<div><strong>Duration:</strong> ${escapeHtml(input.durationMs)}ms</div>` : ''}
    <div><strong>${escapeHtml(safeCountLabel)}:</strong> ${safeRows.length}</div>
    <div><strong>${escapeHtml(specialCountLabel)}:</strong> ${specialRows.length}</div>
    <div><strong>Generated:</strong> ${escapeHtml(new Date().toISOString())}</div>
  </div>
  ${input.note ? `<div class="note">${escapeHtml(input.note)}</div>` : ''}

  <h2>${escapeHtml(safeLabel)}</h2>
  ${renderTable(safeRows, includeNewValue)}

  <h2>${escapeHtml(specialLabel)}</h2>
  ${renderTable(specialRows, includeNewValue)}

  <script>
    window.onload = function () {
      setTimeout(function () { window.print(); }, 250);
    };
  </script>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    throw new Error('Popup blocked — allow popups to download the PDF report.');
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
